import { Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { Exceptions, routes } from '@blockpulse3/data/shared';
import {
  CompanyCapitalType,
  CompanyStatus,
  CompanyType,
  useCreateCompanyMutation,
  useGetPappersExtractInpiDocumentLazyQuery,
  useGetPappersRegisterExtractDocumentLazyQuery,
  useGetPappersStatutsDocumentLazyQuery,
} from '@blockpulse3/graphql/hooks';
import { useErrorToast } from '@blockpulse3/ui/commons';

import { StepFormContextProvider } from '../../provider';
import { OnboardingStep } from '../OnboardingStep';
import { ExistingCompanyInformations } from '../forms';
import { CompanyInformationsForm } from '../forms/ExistingCompanyInformations/types';

type Props = unknown;

/**
 * OnboardingExistingCompanyCreate.
 * Existing company informations, create step.
 *
 * @returns {JSX.Element}
 */
export function OnboardingExistingCompanyCreate(): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nOnboardingExistingCompanyView = useTranslations('OnboardingExistingCompanyView');
  const i18nCompanyInformationsForm = useTranslations('CompanyInformationsForm');

  const errorToast = useErrorToast();

  const navigate = useNavigate();

  const [createCompanyMutation] = useCreateCompanyMutation();

  const [getPappersExtractInpiDocument] = useGetPappersExtractInpiDocumentLazyQuery();
  const [getPappersStatutsDocument] = useGetPappersStatutsDocumentLazyQuery();
  const [getPappersRegisterExtractDocument] = useGetPappersRegisterExtractDocumentLazyQuery();

  const handleCreateExistingCompany = (data: CompanyInformationsForm): void => {
    const { address, country, capitalType, ...rest } = data;

    createCompanyMutation({
      variables: {
        createCompanyInput: {
          status: CompanyStatus.DRAFT,
          type: CompanyType.COMPANY,
          address: {
            line: address.line,
            city: address.city,
            postalCode: address.postalCode,
            country: address.country.value,
          },
          country: country.value,
          capitalType: capitalType?.value as CompanyCapitalType,
          ...rest,
        },
      },
      onCompleted: (data) => {
        const companyId = data.createCompany.id;
        if (data.createCompany.registrationNumber) {
          const args = {
            variables: {
              companyId,
            },
          };
          getPappersExtractInpiDocument(args);
          getPappersStatutsDocument(args);
          getPappersRegisterExtractDocument(args);
        }
        window.history.replaceState(null, '', routes.onboardings.company.infos.fmt(companyId));
        navigate(routes.onboardings.company.representatives.fmt(companyId));
      },
      onError: (error) => {
        error.message === Exceptions.AlreadyExistingCompany
          ? errorToast({
              title: i18nOnboardingExistingCompanyView('Toast.AlreadyExistingCompanyError'),
            })
          : errorToast({ title: i18nOnboardingExistingCompanyView('Toast.Error') });
      },
    });
  };

  return (
    <StepFormContextProvider>
      <OnboardingStep
        subTitle={i18nCompanyInformationsForm('FormSubTitle')}
        title={i18nCompanyInformationsForm('FormTitle')}
      >
        <ExistingCompanyInformations onSubmit={handleCreateExistingCompany} />
        <Button form="company-informations" type="submit" w="full">
          {i18nGeneral('Submit')}
        </Button>
      </OnboardingStep>
    </StepFormContextProvider>
  );
}

export type OnboardingExistingCompanyCreateProps = Props;
